import { http } from 'cdui-js';

// 账户列表
export const fetchAccounts = async () => {
  const res = await http.get(`/2go/user/accounts`);
  return res;
};

// 获取综合资产 (用户级)
export function fetchCashBalances() {
  return http.get(`/2go/user/account/cashBalances?currency=HKD`) as any;
}

// 获取指定交易账户现金余额
export function fetchCashBalanceByAcc(accountNum) {
  return http.get(`/2go/user/account/${accountNum}/cashBalances?combine=true&currency=HKD&market=HK`) as any;
}

// 购买力
export function fetchBuyPowers(accountNum) {
  return http.get(`/2go/user/account/${accountNum}/buyPowers?currency=HKD`);
}

export type QuoteCurrency = 'HKD' | 'USD' | 'CNY';

export type AccountCashBalanceQuery = {
  /** 证券首页传 true */
  combine?: boolean;
  currency: QuoteCurrency;
  /** 证券首页 HK,US,MA,MZ；不传=全部市场；加密户传 HSK */
  market?: string;
};

/** 按当前户、当前计价币折算现金。首页大数字用这个，不要读 overview */
export function fetchAccountCashBalances(accountNum: string, query: AccountCashBalanceQuery) {
  const params = new URLSearchParams();
  if (query.combine != null) params.set('combine', String(query.combine));
  params.set('currency', query.currency);
  if (query.market) params.set('market', query.market);
  return http.get(
    `/2go/user/account/${encodeURIComponent(accountNum)}/cashBalances?${params.toString()}`,
  ) as Promise<{
    netAsset?: number | string;
    ledgerCashBal?: number | string;
    lockupCashBal?: number | string;
    availCashBal?: number | string;
    withdrawalBal?: number | string;
    profitAndLoss?: number | string;
    profitAndLossRatio?: number | string;
    stockMarketValue?: { amount?: number | string; currency?: string };
  }>;
}

/** 最大购买力。overview 里的购买力只作兜底，不要优先用 */
export function fetchAccountBuyPowers(accountNum: string, currency: QuoteCurrency) {
  return http.get(
    `/2go/user/account/${encodeURIComponent(accountNum)}/buyPowers?currency=${currency}`,
  ) as Promise<{ buyPower?: number | string }>;
}

// 持仓信息列表 （用户级）
export function fetchStockBalances() {
  return http.get(`/2go/user/account/stockBalances`);
}

// 持仓信息列表 交易账户
export function fetchStockBalancesByAcc(accountNum) {
  return http.get(`/2go/user/account/${accountNum}/stockBalances?market=HK`);
}

// /2go/currencyExchanges/availableCurrencies 查询当前账户可兑换货币
export const fetchAvaCurrencies = async (accountNum) => {
  const res = await http.get(`/2go/currencyExchanges/availableCurrencies?accountNum=${accountNum}`);
  return res;
};

/** 货币兑换：指定账户 + 币种的可兑金额（data 为 number）。currency 必填。 */
export const fetchCashBalancesByCurrency = async (accountNum: string, currency: string) => {
  const num = String(accountNum || '').trim();
  const cur = String(currency || '').trim();
  if (!num || !cur) {
    throw new Error('fetchCashBalancesByCurrency: accountNum and currency are required');
  }
  return http.get(
    `/2go/user/account/${encodeURIComponent(num)}/cashBalancesByCurrency?currency=${encodeURIComponent(cur)}`,
  ) as Promise<number>;
};

export const fetchCurrencyRates = async (from, to) => {
  const res = await http.get(`/2go/currencyExchanges/rates?from=${from}&to=${to}`);
  return res;
};

// 提交货币兑换
export const submitCurrencyExchange = async (data: {
  accountNum: string;
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number;
  exchangeRate: number;
  toAmount: number;
}) => {
  const res = await http.post(`/2go/currencyExchanges`, data);
  return res;
};

// 加开账户申请列表
export const fetchApplyAccList = async () => {
  const res = await http.get(`/user/account/apply/getListByUserId?applyType=ADD_ACCOUNT`);
  return res;
};

/** 已完成开户申请的证券账户类型（status=FINISHED，去重） */
export const fetchFinishedSecuritiesAccountTypes = async (): Promise<string[]> => {
  const res = (await http.get(`/user/account/apply/finishedSecuritiesAccountTypes`)) as any;

  const list = res?.data ?? res ?? [];
  return Array.isArray(list) ? list.filter(Boolean) : [];
};

/** 专业投资者认证前置状态（含是否大陆居民 isMainlandIdentity） */
export interface ProfessionalInvestorAssessmentStatus {
  hasRiskAssessment?: boolean;
  hasProfessionalInvestorCertification?: boolean;
  professionalInvestorCertificationStatus?: string | null;
  /** 是否中国大陆居民；根据 user_base.identity_type 是否为 MAINLAND_CHINA 判断 */
  isMainlandIdentity?: boolean | null;
}

export const fetchProfessionalInvestorAssessmentStatus = async () => {
  const res = (await http.get(`/user/professionalInvestor/assessment/status`)) as any;
  return (res?.data ?? res ?? {}) as ProfessionalInvestorAssessmentStatus;
};

// 获取原来的证券账户类型和基金账号类型
export const fetchAccountTypes = async () => {
  const res = await http.get(`/user/account/account/getByUserId`);
  return res;
};

// 获取银行列表 /user/edda/bankList
export interface BanksItem {
  id: string;
  bindId: string;
  icon: string;
  name: string;
  account: string;
  encryptAccount: string;
  types: string[];
  bankCountry: string;
  bankCode: string;
}

export const fetchEddaBanks = async () => {
  const res = (await http.get(`/user/edda/bankList`)) as any;

  return res.map((item) => ({
    id: item.id,
    bindId: item.bindId,
    icon: '',
    name: item.bankName,
    account: item.bankAccountNo,
    encryptAccount: item.bankAccountNoEncrypt,
    types: item.bankAccountType ? item.bankAccountType.split(',') : [],
  }));
};

///user/edda/save 保存银行卡
export const saveBank = async (data) => {
  const res = await http.post(`/user/edda/save`, data);
  return res;
};

// 解绑银行卡 /user/edda/unbind
export const unbindBank = async (data) => {
  const res = await http.post(`/user/edda/unbind`, data);
  return res;
};

// 提交edda申请

export const submitEdda = async (data) => {
  const res = await http.post(`/user/edda/submit`, data);
};

// 银行列表（市场字典） /2go/market/banks
export const fetchMarketBanks = async (currency: string) => {
  const res = await http.get(`/2go/market/banks?currency=${currency}`);
  return res;
};
